<template>
  <lf-modal :close="handleCancel" large :title="modalTitle">
    <template #content>
      <div class="space-y-5 px-6 pb-2">
        <tracking-token-inputs
          v-if="type === DynamicFieldModalType.Resume"
          :initial-values="initialTokenValues"
          @value:change="tokenValues = $event"
        />
        <widget-token-inputs
          v-else-if="type === DynamicFieldModalType.Widget"
          :initial-values="initialWidgetValues"
          @value:change="widgetValues = $event"
        />
        <url-parameters-section
          ref="urlParamsSectionRef"
          v-model:parameters="urlParameters"
        />
      </div>
      <hr class="border-gray-200" />
      <div class="flex items-center justify-between p-4 px-6">
        <div class="flex items-center gap-2">
          <lf-switch
            v-model="applyToAll"
            name="apply_to_all"
            class="flex-none"
          />
          <span class="text-sm text-gray-600">
            {{
              $t("ORGANIZATION.COMMUNICATION_TEMPLATES.APPLY_URL_PARAMS_TO_ALL")
            }}
          </span>
        </div>
        <modal-buttons
          :on-cancel="handleCancel"
          :on-save="saveValues"
          :disabled="hasValidationErrors"
        />
      </div>
    </template>
  </lf-modal>
</template>
<script setup lang="ts">
import { computed, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

import { v4 as uuid } from "uuid";

import { DynamicFieldModalType } from "@/enums/emailCustomization";
import {
  UTM_DYNAMIC_PARAMETERS_MAP,
  WIDGET_FIELD_IDS,
} from "@/helpers/constants/tracking";
import { appTrackingTokenKey } from "@/helpers/textEditor";

import UrlParametersSection from "@/components/deals/bp-link-generator/UrlParametersSection.vue";
import TrackingTokenInputs from "@/components/dynamicFields/TrackingTokenInputs.vue";
import WidgetTokenInputs from "@/components/dynamicFields/WidgetTokenInputs.vue";
import LfModal from "@/components/ui/Modal.vue";
import ModalButtons from "@/components/ui/ModalButtons.vue";
import LfSwitch from "@/components/ui/inputs/LfSwitch.vue";

import type { UrlParameter } from "@/models/urlParameters";

export interface TokenModalPayload {
  value: string;
  applyToAll: boolean;
  urlParamTokens: string[];
}

const WIDGET_FIELD_KEYS = WIDGET_FIELD_IDS.map((fieldId) =>
  fieldId.replace("=", "")
);

const REVERSE_UTM_MAP: Record<string, string> = Object.fromEntries(
  Object.entries(UTM_DYNAMIC_PARAMETERS_MAP).map(([key, value]) => [
    value.replace("=", ""),
    key,
  ])
);

const parseInitialParams = (params: string) => {
  const widgetVals: Record<string, string> = {};
  const tokenVals: string[] = [];
  const urlParams: UrlParameter[] = [];

  if (!params) {
    return { widgetVals, tokenVals, urlParams };
  }

  const cleaned = params.replace(/]$/, "");
  const parts = cleaned.split(",");

  for (const part of parts) {
    const eqIndex = part.indexOf("=");
    if (eqIndex === -1) {
      continue;
    }

    const key = part.substring(0, eqIndex);
    const value = part.substring(eqIndex + 1);

    if (key === "appTrackingToken") {
      tokenVals.push(value);
    } else if (WIDGET_FIELD_KEYS.includes(key)) {
      widgetVals[`${key}=`] = value;
    } else if (key in REVERSE_UTM_MAP) {
      urlParams.push({
        id: uuid(),
        type: REVERSE_UTM_MAP[key],
        customKey: "",
        value,
      });
    } else {
      urlParams.push({ id: uuid(), type: "other", customKey: key, value });
    }
  }

  return { widgetVals, tokenVals, urlParams };
};

const {
  type,
  linkName,
  initialParams = "",
} = defineProps<{
  type: DynamicFieldModalType;
  linkName: string;
  initialParams?: string;
}>();

const emit = defineEmits<{
  "value:save": [payload: TokenModalPayload];
}>();

const { t } = useI18n();

const parsed = parseInitialParams(initialParams);

const tokenValues = ref<{ value: string; id: string }[]>([]);
const initialWidgetValues = parsed.widgetVals;
const initialTokenValues = parsed.tokenVals;
const widgetValues = ref<{ value: string; id: string }[]>([]);
const urlParameters = ref<UrlParameter[]>(
  parsed.urlParams.length
    ? parsed.urlParams
    : [{ id: uuid(), type: "", customKey: "", value: "" }]
);
const urlParamsSectionRef = useTemplateRef<
  InstanceType<typeof UrlParametersSection>
>("urlParamsSectionRef");
const applyToAll = ref(false);

const modalTitle = computed(() =>
  t("ORGANIZATION.COMMUNICATION_TEMPLATES.SET_UP_LINK_TITLE", {
    name: linkName,
  })
);

const hasValidationErrors = computed(
  () => urlParamsSectionRef.value?.hasValidationErrors ?? false
);

const isResumeField = computed(() => type === DynamicFieldModalType.Resume);

const getValues = (
  values: { value: string; id: string }[],
  valueType: "resume" | "widget"
) => {
  return values.reduce((tokens: string[], { id, value }) => {
    if (!value) {
      return tokens;
    }

    const key = valueType === "resume" ? appTrackingTokenKey : id;
    tokens.push(`${key}${value}`);
    return tokens;
  }, []);
};

const getUrlParameterValues = () => {
  return urlParameters.value.reduce((tokens: string[], param) => {
    if (!param.type || !param.value.trim()) {
      return tokens;
    }

    const key = param.type === "other" ? param.customKey.trim() : param.type;
    if (!key) {
      return tokens;
    }

    const utmKey =
      key in UTM_DYNAMIC_PARAMETERS_MAP
        ? UTM_DYNAMIC_PARAMETERS_MAP[
            key as keyof typeof UTM_DYNAMIC_PARAMETERS_MAP
          ]
        : `${key}=`;

    tokens.push(`${utmKey}${param.value}`);
    return tokens;
  }, []);
};

const saveValues = () => {
  const resumeTokensWithValues = getValues(tokenValues.value, "resume");
  const widgetTokensWithValues = getValues(widgetValues.value, "widget");
  const urlParamTokens = getUrlParameterValues();

  const mainTokensWithValues = isResumeField.value
    ? resumeTokensWithValues
    : widgetTokensWithValues;

  const allTokens = [...mainTokensWithValues, ...urlParamTokens];
  const value = allTokens.length ? `__${allTokens.join(",")}` : "";

  emit("value:save", {
    value,
    applyToAll: applyToAll.value,
    urlParamTokens,
  });
};

const handleCancel = () => {
  emit("value:save", { value: "", applyToAll: false, urlParamTokens: [] });
};
</script>
